/* =====================================================================
   3D PLATFORM MODEL — canvas drawing engine (no libraries).
   Four stacked planes rotate slowly; signals travel from the sewing
   machines (bottom) through the data layer to the role screens.

   The model always fits its box: the scale is worked out from the box size
   for every rotation angle, so nothing is cropped on phones, tablets or
   large screens.

   createPlatformModel(canvas, stage, { text, onAutoLayer })
     → { setActive(i, byUser), setText(text), destroy() }
   ===================================================================== */

const C = { mint: "#16E6B2", cyan: "#1EC8FF", line: "rgba(157,178,189,", text: "#9DB2BD", white: "#E8EFF2" };
const LAYERS = [
  { y: -1.2, fill: "rgba(30,200,255,.11)" },
  { y: -0.4, fill: "rgba(30,200,255,.10)" },
  { y: 0.4, fill: "rgba(22,230,178,.10)" },
  { y: 1.2, fill: "rgba(22,230,178,.04)", planned: true },
];
const TILT = 0.52;
const D = 6;
const MACHINE_H = 0.09; // height of the little machine boxes

export function createPlatformModel(canvas, stage, { text, onAutoLayer }) {
  const ctx = canvas.getContext("2d");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let labels = text;

  const machines = [];
  for (let r = 0; r < 4; r++) for (let c = 0; c < 6; c++) machines.push({ x: -0.78 + c * 0.312, z: -0.6 + r * 0.4, hot: 0 });
  const nodes = [{ x: 0, z: 0 }];
  for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; nodes.push({ x: Math.cos(a) * 0.6, z: Math.sin(a) * 0.6, hot: 0 }); }
  const apps = [{ x: -0.62, z: 0, flash: 0 }, { x: 0, z: 0, flash: 0 }, { x: 0.62, z: 0, flash: 0 }];
  const plans = [{ x: -0.45, z: -0.45 }, { x: 0.45, z: -0.45 }, { x: -0.45, z: 0.45 }, { x: 0.45, z: 0.45 }];
  const particles = [];
  const rings = []; // pulses on the data hub

  let W = 0, H = 0, DPR = 1, theta = 0.65, drag = null, active = 0, userPicked = false;
  let raf = 0, last = 0, acc = 0, cycle = 0, visible = false, time = 0;
  let K = 1, CX = 0, CY = 0, small = false, fontPx = 11, count = 0;

  /* --- projection (unit scale) --- */
  const raw = (x, y, z, th) => {
    const x1 = x * Math.cos(th) - z * Math.sin(th);
    const z1 = x * Math.sin(th) + z * Math.cos(th);
    const sy = y * Math.cos(TILT) + z1 * Math.sin(TILT);
    const depth = z1 * Math.cos(TILT) - y * Math.sin(TILT);
    const s = D / (D + depth);
    return [x1 * s, -sy * s, s];
  };
  const P = (x, y, z) => { const r = raw(x, y, z, theta); return [CX + r[0] * K, CY + r[1] * K, r[2]]; };

  /* --- fit the whole model (all rotation angles) into the canvas --- */
  const fit = () => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let a = 0; a < 48; a++) {
      const th = (a / 48) * Math.PI * 2;
      for (const y of [LAYERS[0].y - 0.02, LAYERS[3].y + 0.02]) for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        const r = raw(x, y, z, th);
        minX = Math.min(minX, r[0]); maxX = Math.max(maxX, r[0]); minY = Math.min(minY, r[1]); maxY = Math.max(maxY, r[1]);
      }
    }
    small = W < 560 * DPR;
    fontPx = small ? 9.5 : 11;
    const labelSpace = small ? 0 : Math.min(150 * DPR, W * 0.24); // labels sit to the right on wide screens
    const padX = (small ? 4 : 10) * DPR, padTop = (small ? 26 : 14) * DPR, padBottom = (small ? 26 : 30) * DPR; // bottom leaves room for the hint line
    const availW = W - padX * 2 - labelSpace, availH = H - padTop - padBottom;
    K = Math.min(availW / (maxX - minX), availH / (maxY - minY));
    CX = padX + (availW - (maxX - minX) * K) / 2 - minX * K;
    CY = padTop + (availH - (maxY - minY) * K) / 2 - minY * K;
  };

  const size = () => {
    const r = canvas.getBoundingClientRect();
    DPR = Math.min(2, window.devicePixelRatio || 1);
    W = canvas.width = Math.max(1, r.width * DPR);
    H = canvas.height = Math.max(1, r.height * DPR);
    fit();
    if (reduce || !visible) draw();
  };

  /* --- small drawing helpers --- */
  const poly = (pts, fill, stroke, dash, lw = 1) => {
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
    ctx.closePath();
    if (fill) { ctx.fillStyle = fill; ctx.fill(); }
    if (stroke) { ctx.setLineDash(dash || []); ctx.strokeStyle = stroke; ctx.lineWidth = lw * DPR; ctx.stroke(); ctx.setLineDash([]); }
  };
  const quad = (cx, y, cz, hw, hd) => [P(cx - hw, y, cz - hd), P(cx + hw, y, cz - hd), P(cx + hw, y, cz + hd), P(cx - hw, y, cz + hd)];
  const ring = (cx, y, cz, r, n = 28) => { const pts = []; for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2; pts.push(P(cx + Math.cos(a) * r, y, cz + Math.sin(a) * r)); } return pts; };
  const line = (a, b, stroke, lw = 1, dash) => { ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.setLineDash(dash || []); ctx.strokeStyle = stroke; ctx.lineWidth = lw * DPR; ctx.stroke(); ctx.setLineDash([]); };

  /* a small box = one sewing machine with a signal unit */
  const machineBox = (m, y) => {
    const hw = 0.07, hd = 0.05, top = y + MACHINE_H;
    const b = quad(m.x, y, m.z, hw, hd), t = quad(m.x, top, m.z, hw, hd);
    const hot = m.hot > 0.05;
    // sides
    for (let i = 0; i < 4; i++) { const j = (i + 1) % 4; poly([b[i], b[j], t[j], t[i]], hot ? `rgba(22,230,178,${0.12 + m.hot * 0.3})` : "rgba(16,45,63,.9)", `${C.line}.35)`); }
    poly(t, hot ? `rgba(22,230,178,${0.35 + m.hot * 0.6})` : "rgba(24,64,88,.98)", hot ? "rgba(22,230,178,.9)" : `${C.line}.6)`);
    if (hot) { const p = P(m.x, top + 0.02, m.z); ctx.beginPath(); ctx.arc(p[0], p[1], 2.2 * DPR, 0, 7); ctx.fillStyle = C.mint; ctx.fill(); }
  };

  const spawn = () => {
    const m = machines[(Math.random() * machines.length) | 0]; m.hot = 1;
    const ni = 1 + ((Math.random() * 6) | 0), n = nodes[ni], ai = (Math.random() * 3) | 0, a = apps[ai];
    const legs = [
      [m.x, LAYERS[0].y + MACHINE_H, m.z, n.x, LAYERS[1].y, n.z],
      [n.x, LAYERS[1].y, n.z, 0, LAYERS[1].y, 0],
      [0, LAYERS[1].y, 0, a.x, LAYERS[2].y, a.z],
    ];
    if (Math.random() < 0.18) { const p = plans[(Math.random() * 4) | 0]; legs.push([a.x, LAYERS[2].y, a.z, p.x, LAYERS[3].y, p.z]); }
    particles.push({ legs, t: 0, speed: 0.011 + Math.random() * 0.006, node: n, app: a, leg: 0 });
    count++;
  };

  const alphaFor = (i) => (active === i ? 1 : 0.6);

  function draw() {
    if (!W) return;
    ctx.clearRect(0, 0, W, H);
    ctx.font = `500 ${fontPx * DPR}px "IBM Plex Mono", "Noto Sans KR", ui-monospace, monospace`;

    // corner posts
    [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([x, z]) => line(P(x, LAYERS[0].y, z), P(x, LAYERS[3].y, z), `${C.line}.12)`));

    LAYERS.forEach((L, i) => {
      ctx.globalAlpha = alphaFor(i);
      const q = quad(0, L.y, 0, 1, 1);
      poly(q, L.fill, active === i ? "rgba(22,230,178,.8)" : `${C.line}.35)`, L.planned ? [6 * DPR, 6 * DPR] : null, active === i ? 1.4 : 1);

      if (i === 0) {
        // scanning light across the floor
        const sz = -1 + ((time * 0.00035) % 2);
        line(P(-1, L.y, sz), P(1, L.y, sz), "rgba(22,230,178,.35)", 2);
        // machines, back to front so nearer boxes draw over farther ones
        [...machines].sort((a, b) => P(b.x, L.y, b.z)[2] - P(a.x, L.y, a.z)[2]).forEach((m) => { machineBox(m, L.y); m.hot *= 0.94; });
      }
      if (i === 1) {
        nodes.slice(1).forEach((n) => line(P(n.x, L.y, n.z), P(0, L.y, 0), "rgba(30,200,255,.35)", 1, [3 * DPR, 4 * DPR]));
        poly(ring(0, L.y, 0, 0.6), null, "rgba(30,200,255,.18)", [2 * DPR, 5 * DPR]);
        rings.forEach((r) => poly(ring(0, L.y, 0, 0.1 + r.t * 0.5), null, `rgba(30,200,255,${0.6 * (1 - r.t)})`, null, 1.4));
        nodes.forEach((n, k) => {
          const p = P(n.x, L.y, n.z);
          ctx.beginPath(); ctx.arc(p[0], p[1], (k ? 4 + (n.hot || 0) * 3 : 8) * DPR * p[2], 0, 7);
          ctx.fillStyle = k ? (n.hot > 0.05 ? `rgba(30,200,255,${0.3 + n.hot * 0.5})` : "#0B2434") : "rgba(30,200,255,.3)"; ctx.fill();
          ctx.strokeStyle = C.cyan; ctx.lineWidth = 1.4 * DPR; ctx.stroke();
          if (k) n.hot *= 0.93;
        });
      }
      if (i === 2) {
        // beams from the data hub up to each screen
        apps.forEach((a) => line(P(0, LAYERS[1].y, 0), P(a.x, L.y, a.z), "rgba(22,230,178,.12)", 1, [2 * DPR, 6 * DPR]));
        apps.forEach((a, ai) => {
          poly(quad(a.x, L.y, a.z, 0.24, 0.32), a.flash > 0.05 ? `rgba(22,230,178,${0.12 + a.flash * 0.3})` : "rgba(11,36,52,.95)", `rgba(22,230,178,${0.55 + a.flash * 0.45})`, null, 1 + a.flash);
          for (let b = 0; b < 3; b++) {
            const z = a.z - 0.16 + b * 0.13;
            const len = 0.12 + ((b * 37 + ai * 23) % 10) / 60 + (b === 0 ? a.flash * 0.08 : 0);
            line(P(a.x - 0.15, L.y, z), P(a.x - 0.15 + len, L.y, z), b === 1 ? C.cyan : C.mint, 2.2 * P(a.x, L.y, z)[2]);
          }
          a.flash *= 0.92;
        });
      }
      if (i === 3) plans.forEach((pl, k) => {
        ctx.lineDashOffset = -time * 0.01 * (k % 2 ? 1 : -1);
        poly(ring(pl.x, L.y, pl.z, 0.13, 20), null, "rgba(22,230,178,.6)", [3 * DPR, 3 * DPR]);
        ctx.lineDashOffset = 0;
      });

      // layer label
      const name = labels.layers[i] + (L.planned ? ` · ${labels.planned}` : "");
      ctx.fillStyle = active === i ? C.mint : C.text;
      if (small) {
        // phones: only the highlighted layer is labelled (the list above the model names them all)
        if (active === i) {
          const left = q.reduce((a, b) => (b[0] < a[0] ? b : a));
          ctx.textAlign = "left";
          ctx.fillText(name, Math.max(4 * DPR, left[0]), left[1] - 6 * DPR);
        }
      } else {
        const right = q.reduce((a, b) => (b[0] > a[0] ? b : a));
        ctx.textAlign = "left";
        ctx.fillText(name, Math.min(right[0] + 10 * DPR, W - ctx.measureText(name).width - 4 * DPR), right[1] + 4 * DPR);
      }
      ctx.globalAlpha = 1;
    });

    // screen names
    ctx.globalAlpha = alphaFor(2); ctx.textAlign = "center"; ctx.fillStyle = C.white;
    if (!small || active === 2) apps.forEach((a, ai) => { const p = P(a.x, LAYERS[2].y, a.z + 0.42); ctx.fillText(labels.apps[ai], p[0], p[1] + 12 * DPR); });
    ctx.globalAlpha = 1;

    // signals
    particles.forEach((pt) => {
      const li = Math.min(pt.legs.length - 1, pt.t | 0), f = pt.t - li, g = pt.legs[li];
      const at = (u) => P(g[0] + (g[3] - g[0]) * u, g[1] + (g[4] - g[1]) * u, g[2] + (g[5] - g[2]) * u);
      const h = at(f), tl = at(Math.max(0, f - 0.25));
      const col = li >= 2 ? C.mint : C.cyan;
      line(tl, h, col, 1.6); ctx.globalAlpha = 1;
      ctx.beginPath(); ctx.arc(h[0], h[1], 3 * DPR, 0, 7);
      ctx.fillStyle = col; ctx.shadowColor = col; ctx.shadowBlur = 12 * DPR; ctx.fill(); ctx.shadowBlur = 0;
    });

    // small live counter (top-left)
    ctx.textAlign = "left"; ctx.fillStyle = C.text;
    ctx.fillText(`${labels.counter || "SIGNALS"} ${String(count).padStart(4, "0")}`, 8 * DPR, 18 * DPR);
  }

  function tick(ts) {
    const dt = Math.min(50, ts - (last || ts)); last = ts; time += dt;
    if (!drag) theta += (0.0022 * dt) / 16;
    acc += dt; if (acc > 140) { acc = 0; spawn(); }
    particles.forEach((p) => {
      p.t += (p.speed * dt) / 16;
      const leg = p.t | 0;
      if (leg !== p.leg) {
        if (p.leg === 0) p.node.hot = 1;                       // reached a data node
        if (p.leg === 1) rings.push({ t: 0 });                 // reached the hub
        if (p.leg === 2) p.app.flash = 1;                      // reached a role screen
        p.leg = leg;
      }
    });
    for (let i = particles.length - 1; i >= 0; i--) if (particles[i].t >= particles[i].legs.length) particles.splice(i, 1);
    rings.forEach((r) => { r.t += dt / 900; });
    for (let i = rings.length - 1; i >= 0; i--) if (rings[i].t >= 1) rings.splice(i, 1);
    if (!userPicked) { cycle += dt; if (cycle > 3800) { cycle = 0; active = (active + 1) % 4; onAutoLayer?.(active); } }
    draw();
    raf = visible ? requestAnimationFrame(tick) : 0;
  }

  const io = new IntersectionObserver(([e]) => {
    visible = e.isIntersecting;
    if (visible && !raf && !reduce) { last = 0; raf = requestAnimationFrame(tick); }
  });
  io.observe(stage);

  const down = (e) => { drag = { x: e.clientX, th: theta }; stage.setPointerCapture?.(e.pointerId); };
  const move = (e) => { if (drag) { theta = drag.th + (e.clientX - drag.x) * 0.008; if (reduce) draw(); } };
  const up = () => { drag = null; };
  stage.addEventListener("pointerdown", down);
  stage.addEventListener("pointermove", move);
  stage.addEventListener("pointerup", up);
  stage.addEventListener("pointercancel", up);
  const ro = new ResizeObserver(size);
  ro.observe(canvas);
  size();
  if (reduce) { for (let i = 0; i < 14; i++) { spawn(); particles[i].t = Math.random() * 3; } draw(); }

  return {
    setActive(i, byUser) { active = i; if (byUser) userPicked = true; if (reduce) draw(); },
    setText(t) { labels = t; draw(); },
    destroy() {
      cancelAnimationFrame(raf); io.disconnect(); ro.disconnect();
      stage.removeEventListener("pointerdown", down); stage.removeEventListener("pointermove", move);
      stage.removeEventListener("pointerup", up); stage.removeEventListener("pointercancel", up);
    },
  };
}

/* Sign-off background: slow dashed "thread" lines. Returns a cleanup function. */
export function createThreads(canvas) {
  const ctx = canvas.getContext("2d");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let w = 0, h = 0, t = 0, raf = 0, visible = false;
  const DPR = Math.min(2, window.devicePixelRatio || 1);
  const size = () => { const r = canvas.getBoundingClientRect(); w = canvas.width = r.width * DPR; h = canvas.height = r.height * DPR; draw(); };
  function draw() {
    ctx.clearRect(0, 0, w, h);
    for (let i = 0; i < 9; i++) {
      const y0 = h * (0.15 + i * 0.09);
      ctx.beginPath();
      for (let x = 0; x <= w; x += 12 * DPR) { const y = y0 + Math.sin(x / (w * 0.22) + i * 0.7 + t) * h * 0.035; x ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }
      ctx.setLineDash([10 * DPR, 8 * DPR]); ctx.lineDashOffset = -t * 60 * (i % 2 ? 1 : -1);
      ctx.strokeStyle = i % 3 === 0 ? "rgba(22,230,178,.30)" : "rgba(30,200,255,.14)"; ctx.lineWidth = 1.4 * DPR; ctx.stroke();
    }
  }
  const loop = () => { t += 0.004; draw(); raf = visible ? requestAnimationFrame(loop) : 0; };
  const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible && !raf && !reduce) raf = requestAnimationFrame(loop); });
  io.observe(canvas);
  window.addEventListener("resize", size);
  size();
  return () => { cancelAnimationFrame(raf); io.disconnect(); window.removeEventListener("resize", size); };
}
